import { useQuery, useMutation } from '@tanstack/react-query'
import { queryKeys } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { useAuthStore } from '../../store/authStore'
import { toast } from 'sonner'
import {
  configPresetsApi,
  ConfigPresetSummary,
  ConfigPresetDetail,
  CreateConfigPresetPayload,
  UpdateConfigPresetPayload,
} from '../../services/api'

/**
 * Hook for fetching preset list, filtered by node type
 */
export function useConfigPresets(nodeTypeId?: string) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.configPresets.list(orgId, nodeTypeId),
    queryFn: (): Promise<ConfigPresetSummary[]> => configPresetsApi.getList(nodeTypeId),
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching a single preset (with config). Disabled until a presetId is provided.
 */
export function useConfigPresetDetail(presetId: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.configPresets.detail(orgId, presetId || ''),
    queryFn: (): Promise<ConfigPresetDetail> => configPresetsApi.getById(presetId!),
    enabled: !!presetId && !!orgId,
  })
}

/**
 * Hook for creating a new org preset
 */
export function useCreateConfigPreset() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (payload: CreateConfigPresetPayload): Promise<ConfigPresetDetail> =>
      configPresetsApi.create(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.configPresets.list(orgId, variables.node_type),
      })
      toast.success('Profile saved successfully')
    },
  })
}

/**
 * Hook for updating an existing org preset
 */
export function useUpdateConfigPreset() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({
      presetId,
      payload,
    }: {
      presetId: string
      payload: UpdateConfigPresetPayload
    }): Promise<ConfigPresetDetail> => configPresetsApi.update(presetId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.configPresets.list(orgId, data.node_type),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.configPresets.detail(orgId, data.id),
      })
      toast.success('Profile updated successfully')
    },
    onError: (error: any) => {
    },
  })
}

/**
 * Hook for deleting an org preset
 */
export function useDeleteConfigPreset() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({
      presetId,
      nodeTypeId,
    }: {
      presetId: string
      nodeTypeId: string
    }): Promise<void> => configPresetsApi.delete(presetId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.configPresets.list(orgId, variables.nodeTypeId),
      })
      toast.success('Profile deleted successfully')
    },
    onError: (error: any) => {
    },
  })
}
